<template>
  <!-- Signed-in account button: a circle (photo or initial) that opens the
       menu for the person's role. Used by both headers
       (components/home/SiteHeader.vue and components/elements/navbar.vue). -->
  <div ref="root" class="account-menu" @keydown.esc="close">
    <button
      type="button"
      class="account-trigger"
      :aria-label="$t('header.myDashboard')"
      aria-haspopup="menu"
      :aria-expanded="open ? 'true' : 'false'"
      @click="open = !open"
    >
      <img v-if="photo" :src="photo" alt="" @error="photoFailed = true" />
      <span v-else aria-hidden="true">{{ initial }}</span>
    </button>

    <transition name="account-pop">
      <div v-if="open" class="account-dropdown" role="menu">
        <div class="account-who">
          <strong>{{ displayName }}</strong>
          <span v-if="user.email && user.email !== displayName">{{ user.email }}</span>
        </div>

        <div class="account-items">
          <template v-for="item in items">
            <router-link
              v-if="item.to"
              :key="item.label"
              :to="{ name: item.to }"
              class="account-item"
              role="menuitem"
              @click.native="close"
            >
              <b-icon pack="fas" :icon="item.icon" size="is-small" />
              <span>{{ item.label }}</span>
            </router-link>
            <button
              v-else
              :key="item.label"
              type="button"
              class="account-item"
              role="menuitem"
              @click="run(item.action)"
            >
              <b-icon pack="fas" :icon="item.icon" size="is-small" />
              <span>{{ item.label }}</span>
            </button>
          </template>
        </div>

        <button type="button" class="account-item is-signout" role="menuitem" @click="signOut">
          <b-icon pack="fas" icon="sign-out-alt" size="is-small" />
          <span>{{ $t("header.signOut") }}</span>
        </button>
      </div>
    </transition>

    <b-modal v-if="isAdmin" v-model="codeModalOpen">
      <GenerateGuiderRegistrationCode @toggleModal="codeModalOpen = $event" />
    </b-modal>
  </div>
</template>

<script>
import { UserActions, UsersStates } from "@/store/Storetypes";
import { dashboardRouteFor } from "@/utils/accountHome";

export default {
  name: "AccountMenu",
  components: {
    GenerateGuiderRegistrationCode: () => import("@/Admin/GenerateGuiderRegistrationCode.vue"),
  },
  data() {
    return { open: false, photoFailed: false, codeModalOpen: false };
  },
  computed: {
    users() {
      return this.$store.state.users;
    },
    user() {
      return this.users[UsersStates.USER] || {};
    },
    isAdmin() {
      return !!this.users[UsersStates.IS_ADMIN];
    },
    displayName() {
      return this.user.displayName || this.user.name || this.user.email || "";
    },
    initial() {
      return this.displayName.trim().charAt(0).toUpperCase() || "•";
    },
    // Old accounts point at an avatar service that no longer exists
    // (avatars.dicebear.com): show the initial instead
    photo() {
      const url = this.user.photoURL;
      if (!url || this.photoFailed || /avatars\.dicebear\.com|api\.dicebear\.com/.test(url)) return "";
      return url;
    },
    // Menu for each role. Labels are English like the dashboards they open;
    // "My dashboard" and "Sign out" are translated.
    items() {
      const s = this.users;
      const dashboard = { to: dashboardRouteFor(s), label: this.$t("header.myDashboard"), icon: "th-large" };
      if (s[UsersStates.IS_ADMIN]) {
        return [
          dashboard,
          { to: "upcoming-activities", label: "Upcoming activities", icon: "tasks" },
          // Hidden for now: { to: "bookings", label: "Bookings stats", icon: "ticket-alt" },
          // Hidden for now: { to: "manage-payments", label: "Payments", icon: "comment-dollar" },
          { to: "manage-activities", label: "Content", icon: "th-list" },
          { to: "coreanos", label: "Pedidos Coreanos", icon: "route" },
          { to: "tuk-guides", label: "Guias (Coreanos)", icon: "user-friends" },
          // Hidden for now: { to: "manage-guiders", label: "Guiders", icon: "users-cog" },
          // Hidden for now: { to: "manage-vendors", label: "Vendors", icon: "store" },
          // Hidden for now: { to: "affiliates-program", label: "Affiliates", icon: "user-friends" },
          { to: "admin-guide-requests", label: "Guide requests", icon: "hand-holding-usd" },
          { to: "admin-private-guides", label: "Private guides", icon: "id-badge" },
          { action: "registrationCode", label: "Send registration code", icon: "unlock" },
        ];
      }
      if (s[UsersStates.IS_EMPLOYEE]) {
        return [
          { to: "guider-upcoming-activities", label: "Upcoming activities", icon: "tasks" },
          { to: "guider-settings", label: "Settings", icon: "user-cog" },
        ];
      }
      if (s[UsersStates.IS_COMPANY]) {
        return [
          dashboard,
          { to: "company-upcoming-activities", label: "Upcoming activities", icon: "tasks" },
          { to: "schedule", label: "Available activities", icon: "calendar-day" },
          { to: "employees", label: "Employees", icon: "users-cog" },
          this.user.hasFinishedSetUp ? { action: "stripe", label: "Stripe dashboard", icon: "credit-card" } : null,
          { to: "company-settings", label: "Settings", icon: "user-cog" },
        ].filter(Boolean);
      }
      if (s[UsersStates.IS_PRIVATE_GUIDE]) {
        return [
          dashboard,
          { to: "guide-portal-profile", label: this.$t("header.myProfile"), icon: "user" },
        ];
      }
      return dashboard.to ? [dashboard] : [];
    },
  },
  watch: {
    open(isOpen) {
      if (isOpen) document.addEventListener("click", this.onOutsideClick, true);
      else document.removeEventListener("click", this.onOutsideClick, true);
    },
    "user.photoURL"() {
      this.photoFailed = false;
    },
  },
  beforeDestroy() {
    document.removeEventListener("click", this.onOutsideClick, true);
  },
  methods: {
    close() {
      this.open = false;
    },
    onOutsideClick(event) {
      if (this.$refs.root && !this.$refs.root.contains(event.target)) this.close();
    },
    run(action) {
      this.close();
      if (action === "registrationCode") this.codeModalOpen = true;
      if (action === "stripe") this.openStripe();
    },
    async openStripe() {
      // Open the tab now (browsers block pop-ups opened after a request)
      const tab = window.open("", "_blank");
      try {
        const response = await fetch(`${process.env.VUE_APP_API_URL}/stripe/dashboard/login`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ accountId: this.user.stripeAccountId }),
        });
        const data = await response.json();
        if (!data.success) throw new Error("no link");
        if (tab) tab.location = data.url;
        else window.location.href = data.url;
      } catch (error) {
        if (tab) tab.close();
        this.$buefy.toast.open({ message: "We couldn't open Stripe. Please try again.", type: "is-danger" });
      }
    },
    signOut() {
      this.close();
      this.$emit("signed-out");
      this.$store.dispatch(UserActions.SIGN_OUT);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/styles/variables";

// Same look as the other header menus (language switcher, phone menu)
.account-menu {
  position: relative;
  font-family: $family-primary;
}

.account-trigger {
  width: 2.5rem;
  height: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  overflow: hidden;
  border: 2px solid $white;
  border-radius: 50%;
  background: $forest;
  box-shadow: 0 0 0 1px $line;
  color: $white;
  font: inherit;
  font-weight: 800;
  font-size: 1rem;
  cursor: pointer;
  transition: box-shadow 0.15s ease;

  &:hover,
  &[aria-expanded="true"] {
    box-shadow: 0 0 0 2px $lime;
  }

  &:focus-visible {
    outline: 2px solid $forest-2;
    outline-offset: 2px;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.account-dropdown {
  position: absolute;
  right: 0;
  top: calc(100% + 0.5rem);
  z-index: 1050;
  width: 16rem;
  max-width: calc(100vw - 2rem);
  max-height: calc(100vh - 6rem);
  overflow-y: auto;
  padding: 0.375rem;
  background: $white;
  border: 1px solid $line;
  border-radius: $radius;
  box-shadow: 0 12px 28px rgba($forest, 0.14);
  text-align: left;
}

.account-who {
  display: flex;
  flex-direction: column;
  padding: 0.5rem 0.75rem 0.625rem;
  margin-bottom: 0.25rem;
  border-bottom: 1px solid $line;
  line-height: 1.35;

  strong {
    color: $forest;
    font-weight: 700;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  span {
    font-size: 0.8125rem;
    color: $ink-sub;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.account-items {
  padding-bottom: 0.25rem;
  margin-bottom: 0.25rem;
  border-bottom: 1px solid $line;

  &:empty {
    display: none;
  }
}

.account-item {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  width: 100%;
  padding: 0.5625rem 0.75rem;
  border: 0;
  border-radius: 6px;
  background: transparent;
  font: inherit;
  font-weight: 600;
  font-size: 0.9375rem;
  color: $forest;
  text-align: left;
  text-decoration: none;
  cursor: pointer;

  .icon {
    color: $ink-sub;
    flex-shrink: 0;
  }

  &:hover,
  &:focus-visible,
  &.router-link-exact-active {
    background: $soft;
    color: $forest;
  }

  &.is-signout {
    color: #b42318;

    .icon {
      color: inherit;
    }
  }
}

.account-pop-enter-active,
.account-pop-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.account-pop-enter,
.account-pop-leave-to {
  opacity: 0;
  transform: translateY(-0.25rem);
}

@media (prefers-reduced-motion: reduce) {
  .account-pop-enter-active,
  .account-pop-leave-active {
    transition: none;
  }
}
</style>
